import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import toastError from '../../toasts/toastError';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  CardHeader,
  Divider,
  List,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
  Badge,
  useTheme,
  ListItemButton,
} from '@mui/material';

import { PushPin, PushPinOutlined, ReportProblem } from '@mui/icons-material';

import { format, isSameDay, parseISO } from 'date-fns';

import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';
import noPicture from '../../assets/nopicture.png';
import { getBackendUrl } from '../../config';

const backendUrl = getBackendUrl();

const DashboardManage = () => {
  const theme = useTheme();
  const classes = getStyled(theme);
  const navigate = useNavigate();
  const { user, socket } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const companyId = user.companyId;
  const isMounted = useRef(true);
  const fetchingRef = useRef(false);

  // Função para buscar tickets
  const fetchTickets = useCallback(async () => {
    if (fetchingRef.current) return; // Evita requisições duplicadas

    try {
      fetchingRef.current = true;
      const { data } = await api.get('/usersMoments');
      if (isMounted.current) {
        setTickets(data);
      }
    } catch (err) {
      if (isMounted.current) {
        if (err.response?.status !== 500) {
          toastError(err);
        } else {
          toastError(`${i18n.t('frontEndErrors.getUsers')}`);
        }
      }
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  // Efeito de montagem - apenas marca como montado
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Busca inicial dos tickets
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Efeito para socket - escuta eventos
  useEffect(() => {
    const onAppMessage = (data) => {
      if (
        data.action === 'create' ||
        data.action === 'update' ||
        data.action === 'delete'
      ) {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, companyId, fetchTickets]);

  // Efeito para scroll horizontal
  useEffect(() => {
    const handleWheel = (e) => {
      const isOverCard = e.target.closest('.card') !== null;

      const mainContainer = document.querySelector('.main-container');
      if (!isOverCard && mainContainer) {
        mainContainer.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  const handleTicketClick = useCallback(
    (ticket) => {
      navigate(`/tickets/${ticket.uuid}`);
    },
    [navigate],
  );

  const sortTickets = useCallback((ticketsList) => {
    return [...ticketsList].sort((a, b) => {
      const aHasPinned = a.pinnedIndex !== undefined && a.pinnedIndex !== null;
      const bHasPinned = b.pinnedIndex !== undefined && b.pinnedIndex !== null;

      if (aHasPinned && bHasPinned) {
        return a.pinnedIndex - b.pinnedIndex;
      }

      if (aHasPinned && !bHasPinned) {
        return -1;
      }

      if (!aHasPinned && bHasPinned) {
        return 1;
      }

      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB - dateA;
    });
  }, []);

  const Moments = useMemo(() => {
    if (tickets && tickets.length > 0) {
      const ticketsByUser = tickets.reduce((userTickets, ticket) => {
        const user = ticket.user;

        if (user) {
          const userIndex = userTickets.findIndex(
            (group) => group.user.id === user.id,
          );
          if (userIndex === -1) {
            userTickets.push({
              user,
              userTickets: [ticket],
            });
          } else {
            userTickets[userIndex].userTickets.push(ticket);
          }
        }
        return userTickets;
      }, []);

      ticketsByUser.forEach((group) => {
        group.userTickets = sortTickets(group.userTickets);
      });

      return ticketsByUser.map((group, index) => (
        <div key={index} style={classes.changeWrap}>
          <Paper elevation={1} sx={classes.cardHeader}>
            <CardHeader
              sx={{ maxWidth: '380px', width: '100%' }}
              avatar={
                <Avatar
                  alt={`${group.user.profileImage}`}
                  src={
                    group.user.profileImage
                      ? `${backendUrl}/public/company${companyId}/user/${group.user.profileImage}`
                      : noPicture
                  }
                />
              }
              title={
                <span>
                  {group?.user?.name || 'Pendentes'} <br />
                  {`Atendimentos: ${group.userTickets?.length}`}
                </span>
              }
            />
          </Paper>
          <Paper elevation={1} sx={{ ...classes.card }} className='card'>
            {group.userTickets.map((ticket) => (
              <List key={ticket.id} sx={{ paddingTop: 0 }}>
                <ListItemButton
                  dense
                  onClick={() => handleTicketClick(ticket)}
                  sx={{ paddingTop: 0 }}
                >
                  <ListItemAvatar>
                    {ticket.pinnedIndex ? (
                      <PushPin
                        sx={{ ...classes.ticketIcon, ...classes.pinIcon }}
                      />
                    ) : (
                      <PushPinOutlined
                        sx={{ ...classes.ticketIcon, ...classes.pinIcon }}
                      />
                    )}
                    <Avatar
                      alt={`${ticket.contact.urlPicture}`}
                      src={`${ticket.contact.urlPicture}`}
                      sx={classes.avatar}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      ticket?.contact?.name?.length > 30
                        ? ticket?.contact?.name.substring(0, 25) + '...'
                        : ticket?.contact?.name
                    }
                    secondary={
                      <Fragment>
                        <div>
                          <Typography
                            sx={{ display: 'inline', fontSize: '11px' }}
                            component='span'
                            variant='body2'
                          >
                            {ticket.lastMessage?.length > 30
                              ? String(ticket.lastMessage).substring(0, 27) +
                                '...'
                              : ticket.lastMessage || '\u00A0'}
                          </Typography>
                        </div>
                        <Badge sx={classes.connectionTag}>
                          {ticket?.whatsapp?.name}
                        </Badge>
                        <Badge
                          sx={{
                            ...classes.connectionTag,
                            backgroundColor: ticket.queue?.color || '#7c7c7c',
                          }}
                        >
                          {ticket.queue?.name.toUpperCase() || 'SEM FILA'}
                        </Badge>
                      </Fragment>
                    }
                  />
                  <Typography
                    sx={{
                      ...(Number(ticket.unreadMessages) > 0
                        ? classes.lastMessageTimeUnread
                        : classes.lastMessageTime),
                      fontSize: '11px',
                    }}
                    component='span'
                    variant='body2'
                  >
                    {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                      <>{format(parseISO(ticket.updatedAt), 'HH:mm')}</>
                    ) : (
                      <>{format(parseISO(ticket.updatedAt), 'dd/MM/yyyy')}</>
                    )}
                  </Typography>
                </ListItemButton>
                <Divider
                  sx={{
                    backgroundColor:
                      theme.palette.mode === 'light' ? '#666666' : '#bdbdbd',
                  }}
                />
              </List>
            ))}
          </Paper>
        </div>
      ));
    } else {
      return null;
    }
  }, [tickets, theme, classes, handleTicketClick, sortTickets, companyId]);

  const MomentsPending = useMemo(() => {
    if (tickets && tickets.length > 0) {
      const pendingTickets = tickets.filter((ticket) => !ticket.user);

      return (
        <div style={classes.main}>
          <div style={classes.changeWrap}>
            <Paper sx={classes.cardHeaderPending}>
              <CardHeader
                sx={{ maxWidth: '380px', width: '100%' }}
                avatar={<Avatar />}
                title={
                  <span>
                    {'Pendentes'}
                    <ReportProblem sx={classes.pending} />
                    <div>Atendimentos: {pendingTickets?.length}</div>
                  </span>
                }
              />
            </Paper>
            <Paper
              square
              elevation={1}
              sx={{ ...classes.card }}
              className='card'
            >
              {pendingTickets.map((ticket) => (
                <List sx={{ paddingTop: 0 }} key={ticket.id}>
                  <ListItemButton
                    dense
                    onClick={() => handleTicketClick(ticket)}
                    sx={{ paddingTop: 0 }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        alt={`${ticket.contact.urlPicture}`}
                        src={`${ticket.contact.urlPicture}`}
                        sx={classes.avatar}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      disableTypography
                      primary={ticket?.contact?.name}
                      secondary={
                        <Fragment>
                          <div>
                            <Typography
                              sx={{ display: 'inline', fontSize: '11px' }}
                              component='span'
                              variant='body2'
                            >
                              {`${
                                ticket.lastMessage?.length > 30
                                  ? String(ticket.lastMessage).substring(
                                      0,
                                      27,
                                    ) + '...'
                                  : ticket.lastMessage || '\u00A0'
                              }`}
                            </Typography>
                          </div>
                          {ticket?.whatsapp?.name && (
                            <Badge sx={classes.connectionTag}>
                              {ticket?.whatsapp?.name}
                            </Badge>
                          )}
                          {ticket?.queue?.name && (
                            <Badge
                              sx={{
                                ...classes.connectionTag,
                                backgroundColor:
                                  ticket.queue?.color || '#7c7c7c',
                              }}
                            >
                              {ticket.queue?.name.toUpperCase() || 'SEM FILA'}
                            </Badge>
                          )}
                        </Fragment>
                      }
                    />
                    <Typography
                      sx={{
                        ...(Number(ticket.unreadMessages) > 0
                          ? classes.lastMessageTimeUnread
                          : classes.lastMessageTime),
                        fontSize: '11px',
                      }}
                      component='span'
                      variant='body2'
                    >
                      {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                        <>{format(parseISO(ticket.updatedAt), 'HH:mm')}</>
                      ) : (
                        <>{format(parseISO(ticket.updatedAt), 'dd/MM/yyyy')}</>
                      )}
                    </Typography>
                  </ListItemButton>
                  <Divider
                    sx={{
                      backgroundColor:
                        theme.palette.mode === 'light' ? '#666666' : '#bdbdbd',
                    }}
                  />
                </List>
              ))}
            </Paper>
          </div>
        </div>
      );
    } else {
      return null;
    }
  }, [tickets, theme, classes, handleTicketClick]);

  return (
    <div
      className='main-container'
      style={{
        display: 'flex',
        flexDirection: 'row',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
      }}
    >
      {MomentsPending}
      {Moments}
    </div>
  );
};

export default DashboardManage;
